import React, { useState, useEffect } from 'react';
import { getTeamData, getWeekStatus } from '../DatabaseAPI';
import { Button } from '../components/ui';
import TeamEditModal from '../components/league/TeamEditModal';
import TeamPlayerCard from '../components/team/TeamPlayerCard';
import './TeamPage.css';

function TeamPage({
    teamId,
    leagueId,
    setPage,
    viewMode = 'own',
    weekStatus: propWeekStatus,
}) {
    const [teamData, setTeamData] = useState(null);
    const [weekStatus, setWeekStatus] = useState(propWeekStatus || null);
    const [loading, setLoading] = useState(true);
    
    // Team Edit Modal State
    const [showEditModal, setShowEditModal] = useState(false);

    const isOpponentView = viewMode === 'opponent';

    // Load team data
    useEffect(() => {
        let effectiveTeamId = teamId;
        
        if (!effectiveTeamId) {
            if (isOpponentView) {
                effectiveTeamId = sessionStorage.getItem('opponentTeamId');
            } else {
                effectiveTeamId = sessionStorage.getItem('selectedTeamId');
            }
        }
        
        if (!effectiveTeamId) {
            console.error('TeamPage: No team ID provided');
            setLoading(false);
            return;
        }

        const loadData = async () => {
            try {
                const team = await getTeamData(effectiveTeamId);
                setTeamData(team);
                
                // Only fetch weekStatus if not provided as prop
                if (!propWeekStatus && leagueId) {
                    const status = await getWeekStatus(leagueId);
                    setWeekStatus(status);
                }
                
                setLoading(false);
            } catch (error) {
                console.error('TeamPage: Error loading team data:', error);
                setLoading(false);
            }
        };
        
        loadData();
    }, [teamId, leagueId, viewMode, isOpponentView, propWeekStatus]);

    const handleNavigation = (destination) => {
        switch(destination) {
            case 'LEAGUES':
                setPage('HOME'); // Go to leagues/teams selection
                break;
            case 'TEAM':
                // Already on team page
                break;
            case 'STANDINGS':
                setPage('LEAGUE'); // Go to standings
                break;
            case 'GEAR':
                setPage('GEAR');
                break;
            case 'LEADERBOARD':
                setPage('LEADERBOARD');
                break;
            default:
                console.error('TeamPage: Unknown navigation destination:', destination);
                break;
        }
    };

    const handlePlayerClick = (player) => {
        // Use id instead of character_sheet_id to avoid off-by-one errors
        // character_sheet_id values are offset and match the previous player's id
        const playerId = player.id;
        sessionStorage.setItem('selectedPlayerId', playerId);
        
        // Store viewMode for PlayerPage to know if this is opponent view
        const effectiveViewMode = viewMode || 'own';
        sessionStorage.setItem('playerViewMode', effectiveViewMode);
        
        setPage('PLAYER');
    };

    const handleTeamNameClick = () => {
        if (!isOpponentView) {
            setShowEditModal(true);
        }
    };

    const handleEditModalClose = () => {
        setShowEditModal(false);
    };

    const handleEditModalSave = async (updatedTeam) => {
        console.log('TeamPage: Team updated:', updatedTeam);
        
        // Update the team data with new values
        setTeamData(prevData => ({
            ...prevData,
            name: updatedTeam.name,
            display_name: updatedTeam.display_name,
            known_as: updatedTeam.known_as,
            hide_known_as: updatedTeam.hide_known_as
        }));
        
        setShowEditModal(false);
    };

    if (loading) {
        return (
            <div className="team-page-loading">
                <h2>Loading Team Data...</h2>
            </div>
        );
    }

    if (!teamData || !teamData.players) {
        return (
            <div className="team-page-error">
                <h2>No Team Data Available</h2>
                <Button onClick={() => setPage('HOME')} variant="secondary">
                    Back to Teams
                </Button>
            </div>
        );
    }

    // Helper function to sort players by position (QB, RB, WR)
    const sortPlayersByPosition = (players) => {
        const positionOrder = { 'QB': 1, 'RB': 2, 'WR': 3 };
        
        return [...players].sort((a, b) => {
            const orderA = positionOrder[a.position] || 999;
            const orderB = positionOrder[b.position] || 999;
            return orderA - orderB;
        });
    };

    // Filter to active players only and sort by position
    const activePlayers = sortPlayersByPosition(
        teamData.players.filter(player => player.active) || []
    );
    
    // Calculate team total
    const teamTotal = activePlayers.reduce((sum, player) => sum + (player.score || 0), 0);

    return (
        <div className="team-page-mobile">
            {/* Top Navigation Bar */}
            <div className="team-top-nav-bar">
                <Button
                    className="team-top-nav-button"
                    onClick={() => handleNavigation('LEAGUES')}
                    variant="secondary"
                >
                    LEAGUES
                </Button>
                <Button
                    className="team-top-nav-button active"
                    onClick={() => handleNavigation('TEAM')}
                    variant="secondary"
                >
                    TEAM
                </Button>
                <Button
                    className="team-top-nav-button"
                    onClick={() => handleNavigation('STANDINGS')}
                    variant="secondary"
                >
                    STANDINGS
                </Button>
                <Button
                    className="team-top-nav-button"
                    onClick={() => handleNavigation('GEAR')}
                    variant="secondary"
                >
                    GEAR
                </Button>
            </div>

            {/* Team Header */}
            <div className="team-header-section">
                <img 
                    src="/art/TeamIcon.png" 
                    alt="Team Icon" 
                    className="team-icon"
                />
                <div className="team-info">
                    <div
                        className={`team-name ${!isOpponentView ? 'clickable' : ''}`}
                        onClick={handleTeamNameClick}
                        title={!isOpponentView ? 'Click to edit team name' : ''}
                    >
                        {teamData.display_name || teamData.name || 'TEAM'}
                    </div>
                    <div className="team-total-label">TEAM TOTAL</div>
                    <div className="team-total-score">{teamTotal.toFixed(1)}PT</div>
                </div>
            </div>

            {/* Player List */}
            <div className="player-list-container">
                {activePlayers.length === 0 ? (
                    <div className="no-players">No active players found</div>
                ) : (
                    activePlayers.map((player) => (
                        <TeamPlayerCard
                            key={player.id}
                            player={player}
                            onClick={handlePlayerClick}
                        />
                    ))
                )}
            </div>

            {/* Team Edit Modal */}
            {showEditModal && teamData && (
                <TeamEditModal
                    isOpen={showEditModal}
                    onClose={handleEditModalClose}
                    onSave={handleEditModalSave}
                    team={{
                        id: teamId || sessionStorage.getItem('selectedTeamId'),
                        name: teamData.name,
                        display_name: teamData.display_name,
                        known_as: teamData.known_as,
                        hide_known_as: teamData.hide_known_as
                    }}
                />
            )}
        </div>
    );
}

export default TeamPage;
